import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { useIsOfflineRecordingEnabled } from '@/app-shell/flags/desktop-flags';
import {
  offlineStorageAvailabilityAtom,
  toOfflineStorageAvailability,
  type OfflineStorageAvailability,
} from '@/atoms/offline-recording';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Reads the boot-time storage status once and publishes it to
 * {@link offlineStorageAvailabilityAtom}. Mount once at the app root.
 *
 * Deliberately a one-shot read with no polling, no retry and no event
 * subscription. The status is decided before the webview loads, on on-disk
 * state this build cannot change, so there is nothing a later read could learn.
 *
 * Headless — it renders nothing — and gated on both `isTauri()` and the
 * offline-recording umbrella flag. The flag gate is what gives this a remote
 * kill switch: the command is new, so a webview running ahead of the desktop
 * binary's command set would fault on it, and with the flag off there is no
 * invoke at all rather than a fault we have to reason about. It costs nothing,
 * because the only surface that reads the result is flag-gated too.
 */
export function useOfflineStorageAvailabilitySync(): void {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const setAvailability = useSetAtom(offlineStorageAvailabilityAtom);

  useEffect(() => {
    if (!isTauri() || !isOfflineRecordingEnabled) {
      return;
    }

    let cancelled = false;
    offlineRecordingBridge
      .storageStatus()
      .then((status) => {
        if (!cancelled) {
          setAvailability(toOfflineStorageAvailability(status));
        }
      })
      .catch((error: unknown) => {
        // Leave the atom `pending`, which keeps the banner hidden. A rejected
        // read means we do not know the answer, and claiming "unavailable"
        // would put a permanent, unactionable banner in front of a clinician
        // whose storage is fine. Staying silent is the pre-existing behaviour
        // this feature improves on, so a failed read degrades to it rather than
        // to a wrong claim — the failure is still reported to the log.
        logger.warn({
          msg: 'Failed to read offline-recording storage status',
          error: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [setAvailability, isOfflineRecordingEnabled]);
}

/**
 * Boot-time availability of offline-recording local storage.
 *
 * Returns `{ state: 'pending' }` until the read resolves, and off desktop —
 * where there is no local database and every offline surface is already hidden
 * by its own `isTauri()` guard. Callers must branch on `state === 'unavailable'`
 * and never on `state !== 'available'`, so a pending read cannot flash an
 * unavailable state at launch.
 */
export function useOfflineStorageAvailability(): OfflineStorageAvailability {
  return useAtomValue(offlineStorageAvailabilityAtom);
}
